import { useSoc, type SocAgent } from '@/api/soc';
import { CompanyCell, Empty, ListPage, PageLoading, Th, tableWrap, theadCls, useListState, type FilterDef } from '@/components/network/NetworkKit';
import { HealthChip, HuntressLink, RmmMatchCell } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { cn } from '@/lib/utils';

/**
 * Every Huntress agent with its health, Defender state and the Datto RMM device it matched
 * (`drmm-` tag → serial → MAC). Stale agents (no callback for the SOC stale threshold) are listed so
 * they can be removed in Huntress; "Not in Datto RMM" finds machines Huntress protects but RMM doesn't.
 */

const FILTERS: FilterDef<SocAgent>[] = [
  { id: 'all',       label: 'All',               test: () => true },
  { id: 'attention', label: 'Needs attention',   test: (a) => a.health !== 'healthy' && a.health !== 'stale' },
  { id: 'silent',    label: 'Not checking in',   test: (a) => a.health === 'silent' },
  { id: 'defender',  label: 'Defender issue', test: (a) => a.health === 'defender' },
  { id: 'no-edr',    label: 'No EDR', test: (a) => a.health === 'no-edr' },
  { id: 'isolated',  label: 'Isolated',          test: (a) => a.isolated },
  { id: 'tamper',    label: 'Tamper off', test: (a) => a.tamperOff },
  { id: 'no-rmm',    label: 'Not in RMM',  test: (a) => !a.rmm && a.health !== 'stale' },
  { id: 'stale',     label: 'Stale',             test: (a) => a.health === 'stale' },
  { id: 'healthy',   label: 'Healthy',           test: (a) => a.health === 'healthy' },
];

export default function SocAgentsPage() {
  const { data, isLoading, error, isFetching } = useSoc({ scoped: true });
  const items = data?.configured ? data.agents : [];
  const l = useListState('cosmos:soc:agents:show', FILTERS, items,
    (a) => `${a.hostname} ${a.orgName} ${a.companyName ?? ''} ${a.serial ?? ''} ${a.os ?? ''} ${a.ip ?? ''} ${a.externalIp ?? ''} ${a.rmm?.hostname ?? ''}`);
  const rows = [...l.rows].sort((a, b) => a.hostname.localeCompare(b.hostname));

  return (
    <ListPage area="SOC" title="Huntress agents" count={data?.configured ? l.rows.length : null} isFetching={isFetching}
      subtitle={data?.configured && <>Not checking in = no callback for {data.settings.silentHours} h; stale = {data.settings.staleDays}+ days (Admin → SOC) · read {fmtRelative(data.fetchedAt)}</>}
      search={{ q: l.q, setQ: l.setQ, placeholder: 'Host, client, serial, IP' }}
      filters={{ defs: FILTERS.filter((f) => f.id === 'all' || f.id === l.show || l.counts[f.id] > 0), show: l.show, pick: l.pick, counts: l.counts }}>
      <PageLoading isLoading={isLoading} error={error} configured={data?.configured} vendor="Huntress" />
      {data?.configured && (
        <div className={tableWrap}>
          <table className="w-full text-sm" style={{ minWidth: 1100 }}>
            <thead className={theadCls}>
              <tr><Th>Host</Th><Th>Client</Th><Th>Health</Th><Th>Last callback</Th><Th>Defender</Th><Th>Datto RMM</Th><Th>Serial</Th><Th>Agent</Th><Th /></tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <Empty cols={9} />}
              {rows.map((a) => (
                <tr key={a.id} className={cn('hover:bg-muted/30', a.health === 'stale' && 'opacity-70')}>
                  <td className="px-3 py-2"><div className="font-medium text-foreground">{a.hostname}</div><div className="max-w-[240px] truncate text-[11px] text-muted-foreground">{a.os ?? a.platform}</div></td>
                  <td className="max-w-[220px] px-3 py-2 text-xs"><CompanyCell companyId={a.companyId} companyName={a.companyName} fallback={a.orgName} /></td>
                  <td className="px-3 py-2"><HealthChip a={a} />{a.tamperOff && <div className="mt-0.5 text-[11px] text-warning">tamper protection off</div>}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground" title={a.lastCallback ?? ''}>{fmtRelative(a.lastCallback)}</td>
                  <td className="max-w-[200px] px-3 py-2 text-xs"><div className="truncate">{a.defender ?? '—'}</div>{a.defenderDetail && <div className="truncate text-[11px] text-muted-foreground">{a.defenderDetail}</div>}</td>
                  <td className="px-3 py-2"><RmmMatchCell a={a} /></td>
                  <td className="max-w-[150px] truncate px-3 py-2 font-mono text-[11px] text-muted-foreground" title={a.serial ?? undefined}>{a.serial ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{a.version ?? '—'}{!a.edrInstalled && <div className="text-[11px] text-warning">no EDR</div>}</td>
                  <td className="px-3 py-2 text-right"><HuntressLink href={a.portalUrl} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListPage>
  );
}
